import Cookies from 'js-cookie';
import { nanoid } from 'nanoid';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ATOZ_ClIENT_REQUEST_ID = nanoid();

/**
 * Enum containing client_ids for supported applications
 */
export enum AtoZClientId {
    LEARN_HUB = 'ATOZ_LEARN_HUB'
}

/**
 * Available deployment stages for AtoZ tenant apps/services.
 * @remarks
 * These stages have been referenced from {@link AtoZShiftManagementApp: https://tiny.amazon.com/1725xd8o7/codeamazpackAtoZblob57a1pack}
 */
export enum Stage {
    DEV = 'dev',
    ESSP = 'essp',
    BETA = 'beta',
    GAMMA = 'gamma',
    PROD = 'prod'
}

/**
 * Maps AtoZ stages to API URLs.
 */
const apiGatewayUrl: { [key in Stage]: string } = {
    dev: 'http://0.0.0.0:4000',
    essp: 'https://atoz-api.integ.amazon.com',
    beta: 'https://atoz-api.integ.amazon.com',
    gamma: 'https://atoz-api.integ.amazon.com',
    prod: 'https://atoz-api.amazon.work'
};

/**
 *
 * @param stage - current deployment stage of the app
 * @param clientId - Client ID value for the API client used for AtoZ interceptor.
 * @returns Headers to be used by API client
 */
export const getApiGatewayHeaders = (
    stage: Stage,
    clientId: AtoZClientId
): Record<string, string> => {
    const headers: Record<string, string> = {
        'x-atoz-client-id': clientId,
        'x-atoz-client-request-id': ATOZ_ClIENT_REQUEST_ID
    };

    /**
     * Read the Cookie value for `atoz-oauth-token`, which is required to authorize calls to
     * atoz-api backend endpoints.
     */
    if (stage !== Stage.PROD) {
        headers.Authorization = `Bearer ${Cookies.get('atoz-oauth-token')}`;
    }

    return headers;
};

/**
 * Katal manifest templating will replace \{\{Stage\}\} with the correct stage value
 * during build time. Bundle for the appropriate stage will have the correct
 * stage string value used for fetching the url
 * https://katal.corp.amazon.com/#/developer/manifest/templatingcdk
 */
export const getCurrentStage = (): Stage => {
    return '{{Stage}}'.toLowerCase() as Stage;
};

/**
 * Wraps {@link apiGatewayUrl} with failsafe to use Beta stage if defined stage is not valid
 */
const apiGatewayUrlWrapper =
    apiGatewayUrl[getCurrentStage()] || apiGatewayUrl[Stage.BETA];

export { apiGatewayUrlWrapper as apiGatewayUrl };
